import * as React from 'react';

import type { InlineButtonData } from '@gitbook/doc-core';
import { Icon, type IconName } from '@gitbook/icons';
import type { Inline } from '@gitbook/slate';
import type { EditorContainer, InlineProps } from '@gitbook/slate-react';
import { Button, useDoubleClick } from '@gitbook/spine-react';

import { type UIResolvedContentReference, useContentReference } from '../../ContentReferences';
import { VoidInlineWithPopover } from '../common';
import { useInlineLinkProps } from '../link/useInlineLinkProp';
import { EditButtonDialogPopover } from './EditButtonPopover';

/**
 * Component to render an inline button.
 */
export function InlineButton(props: InlineProps): React.ReactElement {
    const { node, editor } = props;

    return (
        <VoidInlineWithPopover
            {...props}
            renderSpan={({ openPopover }) => (
                <React.Suspense
                    fallback={
                        <InlineButtonImplementation
                            {...props}
                            resolved={null}
                            openPopover={openPopover}
                        />
                    }
                >
                    <ResolvedInlineButton {...props} openPopover={openPopover} />
                </React.Suspense>
            )}
            popover={<EditButtonDialogPopover node={node} editor={editor} />}
            hoverHighlighting={false}
        />
    );
}

function ResolvedInlineButton(props: {
    node: Inline;
    editor: EditorContainer;
    openPopover: () => void;
}) {
    const { node } = props;
    const { ref } = node.data.toFrozenObject<InlineButtonData>();
    const resolved = useContentReference(ref, { resolveAnchorText: false });

    return <InlineButtonImplementation {...props} resolved={resolved} />;
}

/**
 * The actual implementation of the button, with a resolved action.
 */
function InlineButtonImplementation(props: {
    node: Inline;
    editor: EditorContainer;
    resolved: UIResolvedContentReference | null;
    openPopover: () => void;
}): React.ReactElement | null {
    const { node, resolved, openPopover } = props;
    const { label, kind, icon } = node.data.toFrozenObject<InlineButtonData>();
    const buttonKind = kind === 'primary' ? 'user-primary' : 'user-secondary';

    const inlineProps = useInlineLinkProps(resolved);

    const onClick = useDoubleClick<React.MouseEvent<HTMLButtonElement, MouseEvent>>(
        () => {
            openPopover();
        },
        (event) => {
            inlineProps?.onClick?.(event);
        }
    );

    return (
        <Button
            kind={buttonKind}
            label={label}
            className="inline-flex"
            onClick={onClick}
            leadingIcon={icon ? <Icon icon={icon as IconName} /> : undefined}
        />
    );
}
